import { useState, useCallback, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import RuleTrace from "./RuleTrace";

export default function RulesTab() {
    const { t } = useTranslation();
    const [rules, setRules] = useState([]);
    const [expandedId, setExpandedId] = useState(null);

    const { call: fetchRules, isLoading } = useBluelagoon({
        method: "getIntelligenceRules",
    });
    const { call: updateRule } = useBluelagoon({
        method: "updateIntelligenceRule",
    });

    const load = useCallback(async () => {
        const res = await fetchRules({ limit: 50 });
        if (res?.data?.rules) setRules(res.data.rules);
    }, [fetchRules]);

    useEffect(() => { load(); }, [load]);

    const handleAction = useCallback(async (ruleId, action) => {
        await updateRule({ ruleId, action });
        load();
    }, [updateRule, load]);

    if (isLoading && !rules.length) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-3 mt-4">
            {rules.map((rule) => (
                <div key={rule.id} className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={rule.isActive ? "default" : "outline"}>
                            {rule.isActive
                                ? t("admin.intelligence.rules.active")
                                : t("admin.intelligence.rules.draft")}
                        </Badge>
                        <Badge variant="secondary">{rule.appCategory}</Badge>
                        <Badge variant="outline">{rule.ruleType}</Badge>
                        <span className="text-xs text-muted-foreground">
                            {t("admin.intelligence.rules.confidence")}: {Math.round(rule.confidence * 100)}%
                        </span>
                        <span className="text-xs text-muted-foreground">
                            {rule.exampleCount} {t("admin.intelligence.rules.examples")}
                        </span>
                    </div>
                    <p className="text-sm">{rule.rule}</p>
                    <div className="flex items-center gap-2 pt-1 flex-wrap">
                        {!rule.isActive && (
                            <>
                                <Button size="sm" variant="default" onClick={() => handleAction(rule.id, "approve")}>
                                    <Check className="h-3 w-3 mr-1" />
                                    {t("admin.intelligence.rules.approve")}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleAction(rule.id, "reject")}>
                                    <X className="h-3 w-3 mr-1" />
                                    {t("admin.intelligence.rules.reject")}
                                </Button>
                            </>
                        )}
                        {(rule.reasoning || rule.sourceData) && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setExpandedId(expandedId === rule.id ? null : rule.id)}
                            >
                                {expandedId === rule.id
                                    ? <ChevronUp className="h-3 w-3 mr-1" />
                                    : <ChevronDown className="h-3 w-3 mr-1" />}
                                {t("admin.intelligence.rules.showTrace")}
                            </Button>
                        )}
                    </div>
                    {expandedId === rule.id && <RuleTrace rule={rule} />}
                </div>
            ))}
            {rules.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                    {t("admin.intelligence.rules.noRules")}
                </p>
            )}
        </div>
    );
}
